import { useState, type ComponentProps } from 'react';
import { Button, ButtonGroup, Spinner, Text, Tooltip, useModal } from '@customerio/pluma-components/react';
import type { Delivery } from '@/services/db/delivery/entity';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { channelValueOf, getDeliveryChannel } from '../catalog/channels';
import type { DeliveryRowState } from '../utils/delivery-row-state';
import type { DeliveryRowPermission } from './delivery-row-expansion-hooks';
import { DeliveryResendModal } from './delivery-resend-modal';

export type RowActionCallbacks = {
  onSend: () => void;
  onDelete: () => void;
  onRetry: () => void;
  onRedeliver: (tracked: boolean) => void;
};

type DeliveryRowActionsProps = {
  delivery: Delivery;
  rowState: DeliveryRowState;
  permission: DeliveryRowPermission;
  /** Message retention disabled — blocks resend (its content can't be re-sent). */
  hideBody: boolean;
  /** Event-triggered campaign delivery past the 30-day events-retention window — blocks resend. */
  isExpired: boolean;
  /** Related-model still resolving — matches the info column's spinner. */
  isLoading: boolean;
  callbacks: RowActionCallbacks;
};

/**
 * The action column of the expanded row. The available action is a function of
 * the delivery's state: drafts send or delete, failed/undeliverable retry,
 * already-sent messages resend, and mid-retry deliveries show a status note.
 */
export function DeliveryRowActions({
  delivery,
  rowState,
  permission,
  hideBody,
  isExpired,
  isLoading,
  callbacks,
}: DeliveryRowActionsProps) {
  // Latch immediately on the first click to block a duplicate POST in the window
  // before the optimistic `pending*` flag lands. The flag (set on mutation
  // success, in the query cache) is the durable guard — it survives the expanded
  // row remounting, unlike this local state — so the buttons stay disabled after.
  const [fired, setFired] = useState(false);
  const fire = (action: () => void) => () => {
    setFired(true);
    action();
  };

  if (isLoading) {
    return <Spinner data-test-delivery-row-expansion-button-spinner />;
  }

  const { hasReadOnlyPermission } = permission;
  const locked = hasReadOnlyPermission || fired;

  if (rowState.isDrafted) {
    return (
      <ButtonGroup groupVariant="spaced" size="sm">
        <Button
          data-test-delivery-row-expansion-action-button="send"
          variant="secondary"
          isDisabled={locked || delivery.pendingSend === true}
          onClick={fire(callbacks.onSend)}
        >
          Send
        </Button>
        <Button
          data-test-delivery-row-expansion-action-button="delete"
          variant="secondary"
          isCritical
          isDisabled={locked || delivery.pendingDelete === true}
          onClick={fire(callbacks.onDelete)}
        >
          Delete
        </Button>
      </ButtonGroup>
    );
  }

  if (rowState.isUndeliverable || rowState.isFailed || rowState.isRedeliverableAttempted) {
    return (
      <Button
        data-test-delivery-row-expansion-action-button="retry"
        variant="secondary"
        size="sm"
        isDisabled={locked || delivery.pendingRetry === true}
        onClick={fire(callbacks.onRetry)}
      >
        Retry
      </Button>
    );
  }

  if (rowState.isRedeliverable) {
    return (
      <ResendButton
        delivery={delivery}
        permission={permission}
        hideBody={hideBody}
        isExpired={isExpired}
        onRedeliver={callbacks.onRedeliver}
      />
    );
  }

  if (rowState.isAttempted) {
    return (
      <Text color="subtle" size="sm">
        Retrying soon
      </Text>
    );
  }

  return null;
}

type ResendButtonProps = {
  delivery: Delivery;
  permission: DeliveryRowPermission;
  hideBody: boolean;
  onRedeliver: (tracked: boolean) => void;
  /**
   * The delivery is past the backend's events-retention window for an
   * event-triggered campaign, so it can no longer be resent (Ember
   * `isExpiredDeliveryInEventTriggeredCampaign`). Disabled without a tooltip,
   * matching Ember.
   */
  isExpired?: boolean;
  /** Defaults to the expanded-row look; the detail-preview header renders it primary/md. */
  variant?: ComponentProps<typeof Button>['variant'];
  size?: ComponentProps<typeof Button>['size'];
};

type ResendTooltipArgs = {
  hasSendPermission: boolean;
  forgotten: boolean;
  hideBody: boolean;
  taxonomy: ReturnType<typeof useProductTaxonomy>;
};

function resendTooltip({ hasSendPermission, forgotten, hideBody, taxonomy }: ResendTooltipArgs): string {
  if (!hasSendPermission) {
    return 'Contact your admin to update permissions.';
  }
  if (forgotten) {
    return `Can't resend messages to GDPR-suppressed ${taxonomy.term('customers.plural')}.`;
  }
  if (hideBody) {
    return "Can't resend message because message retention was disabled.";
  }
  return '';
}

export function ResendButton({
  delivery,
  permission,
  hideBody,
  onRedeliver,
  isExpired = false,
  variant = 'secondary',
  size = 'sm',
}: ResendButtonProps) {
  const taxonomy = useProductTaxonomy();
  const modal = useModal(DeliveryResendModal);
  const { hasReadOnlyPermission, hasSendPermission } = permission;
  const { forgotten } = delivery;
  // Latch after a confirm so the row can't be resent twice — the delivery stays in a
  // redeliverable state, so the button alone wouldn't disable (mirrors Ember `redeliveryDisabled`).
  // `resent` guards the in-flight window; `pendingSend` (cache-backed, set on redeliver success)
  // keeps it disabled even if the expanded row remounts and resets `resent`.
  const [resent, setResent] = useState(false);

  const tooltip = resendTooltip({ hasSendPermission, forgotten, hideBody, taxonomy });
  const isDisabled =
    resent ||
    delivery.pendingSend === true ||
    hasReadOnlyPermission ||
    forgotten ||
    hideBody ||
    !hasSendPermission ||
    isExpired;
  const shortName = getDeliveryChannel(channelValueOf(delivery.action_type))?.shortName ?? 'message';

  const openModal = () =>
    modal.open({
      isEmail: delivery.action_type === 'email_action',
      shortName,
      hasReadOnlyPermission,
      onConfirm: (tracked: boolean) => {
        setResent(true);
        onRedeliver(tracked);
      },
    });

  return (
    <Tooltip isCustom content={tooltip} isDisabled={tooltip === ''} placement="top">
      {(context) => (
        <Button
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
          data-test-delivery-redeliver-button
          variant={variant}
          size={size}
          isDisabled={isDisabled}
          onClick={openModal}
        >
          Resend
        </Button>
      )}
    </Tooltip>
  );
}
